/**
 * SocialPulseSearch — /search results page with SocialPulse chrome.
 *
 * Single omni-search across every discoverable surface — places, people,
 * organizations, topics, events, stories, civic issues, memorials,
 * recipes, services and cross-site federated content — merged into one
 * grouped result set.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Search, Loader2, ArrowRight, MapPin, Newspaper, Users, Landmark,
  Calendar, Heart, Home as HomeIcon, Utensils, Wrench, Plane, Camera,
  Globe2, BookOpen,
} from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const TYPE_META = {
  news:          { label: "News",           icon: Newspaper, tint: "text-blue-700",     bg: "bg-blue-50" },
  memorial:      { label: "Memorials",      icon: Heart,     tint: "text-rose-700",     bg: "bg-rose-50" },
  event:         { label: "Events",         icon: Calendar,  tint: "text-orange-700",   bg: "bg-orange-50" },
  cause:         { label: "Causes",         icon: Heart,     tint: "text-red-700",      bg: "bg-red-50" },
  world_changer: { label: "People",         icon: Users,     tint: "text-emerald-700",  bg: "bg-emerald-50" },
  story:         { label: "Stories",        icon: BookOpen,  tint: "text-violet-700",   bg: "bg-violet-50" },
  organization:  { label: "Organizations",  icon: Landmark,  tint: "text-teal-700",     bg: "bg-teal-50" },
  housing:       { label: "Housing",        icon: HomeIcon,  tint: "text-purple-700",   bg: "bg-purple-50" },
  recipe:        { label: "Recipes",        icon: Utensils,  tint: "text-orange-700",   bg: "bg-orange-50" },
  service:       { label: "Services",       icon: Wrench,    tint: "text-slate-700",    bg: "bg-slate-50" },
  travel:        { label: "Travel",         icon: Plane,     tint: "text-cyan-700",     bg: "bg-cyan-50" },
  place:         { label: "Places",         icon: MapPin,    tint: "text-emerald-700",  bg: "bg-emerald-50" },
  person:        { label: "People",         icon: Users,     tint: "text-blue-700",     bg: "bg-blue-50" },
  civic:         { label: "Civic Issues",   icon: Landmark,  tint: "text-emerald-700",  bg: "bg-emerald-50" },
  topic:         { label: "Topics",         icon: Globe2,    tint: "text-slate-700",    bg: "bg-slate-50" },
  photo:         { label: "Photos",         icon: Camera,    tint: "text-fuchsia-700",  bg: "bg-fuchsia-50" },
};

const NETWORK_MAP = {
  news:          { name: "NewsPulse",     canonical: "thenewspulse.pro" },
  memorial:      { name: "FamilyTree",    canonical: "indianfamilytree.com" },
  cause:         { name: "SocialPulse",   canonical: "socialpulse.com" },
  world_changer: { name: "SocialPulse",   canonical: "socialpulse.com" },
  civic:         { name: "CivicPulse",    canonical: "civicpulse.blog" },
  recipe:        { name: "FoodPulse",     canonical: "foodpulse.net" },
  travel:        { name: "WorldPulse",    canonical: "worldpulse.pro" },
  event:         { name: "SocialPulse",   canonical: "socialpulse.com" },
  organization:  { name: "SocialPulse",   canonical: "socialpulse.com" },
  story:         { name: "SocialPulse.blog", canonical: "socialpulse.blog" },
  housing:       { name: "SocialPulse",   canonical: "socialpulse.com" },
  service:       { name: "SocialPulse",   canonical: "socialpulse.com" },
  place:         { name: "SocialPulse",   canonical: "socialpulse.com" },
  person:        { name: "SocialPulse",   canonical: "socialpulse.com" },
  topic:         { name: "SocialPulse",   canonical: "socialpulse.com" },
};

export default function SocialPulseSearch() {
  const [params, setParams] = useSearchParams();
  const paramsQ = params.get("q") || "";
  const [q, setQ] = useState(paramsQ);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const query = paramsQ;
  useEffect(() => {
    document.title = query
      ? `“${query}” · Search · SocialPulse`
      : "Search · SocialPulse";
    setMeta("description", "Search across every SocialPulse property — places, people, organizations, topics, events, stories, civic issues and more, all in one place.");
    setLink("canonical", "https://socialpulse.com/search");
    setMeta("robots", "noindex, follow");
  }, [query]);

  const activeQueryRef = useRef(null);

  useEffect(() => {
    const trimmed = query.trim();
    setQ(trimmed);
    if (trimmed.length < 2) { setData(null); setError(null); setLoading(false); activeQueryRef.current = null; return; }
    if (activeQueryRef.current === trimmed) return; // dedupe StrictMode double-invoke
    activeQueryRef.current = trimmed;
    setLoading(true);
    setError(null);
    const url = `${API}/search?q=${encodeURIComponent(trimmed)}&per_type=8`;
    axios.get(url)
      .then((r) => {
        if (activeQueryRef.current !== trimmed) return;
        setData(r.data);
        setLoading(false);
      })
      .catch(() => {
        if (activeQueryRef.current !== trimmed) return;
        setError("Search is temporarily unavailable. Please try again in a moment.");
        setLoading(false);
      });
  }, [query]);

  function submit(e) {
    e.preventDefault();
    const query = q.trim();
    if (query.length < 2) return;
    setParams({ q: query });
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="sp-search-page">
      <SocialPulseHeader />

      {/* Hero */}
      <section
        className="relative"
        style={{ background: "linear-gradient(180deg, #F0F6FF 0%, #E7F0FA 55%, #F5F9FE 100%)" }}
      >
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-8 sm:pb-10">
          <p className="text-xs font-semibold uppercase tracking-[.22em] text-blue-700">Global Search</p>
          <h1
            className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
            data-testid="sp-search-h1"
          >
            Search the whole <span className="text-blue-700">network.</span>
          </h1>
          <p className="mt-3 sm:mt-4 text-slate-600 text-sm sm:text-base leading-relaxed max-w-2xl">
            Places, people, organizations, topics, events, stories, civic
            issues and cross-site content — one search bar, every pulse.
          </p>

          <form onSubmit={submit} className="mt-6 sm:mt-8" data-testid="sp-search-form">
            <label className="relative block">
              <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Try “Kerala floods”, “community centre”, “volunteer”…"
                data-testid="sp-search-input"
                className="w-full h-14 sm:h-16 pl-12 pr-32 rounded-2xl ring-1 ring-slate-200 bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none text-base sm:text-lg text-slate-800 placeholder:text-slate-400 shadow-sm"
              />
              <button
                type="submit"
                data-testid="sp-search-submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 h-10 sm:h-12 px-4 sm:px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
              >
                Search
              </button>
            </label>
          </form>
        </div>
      </section>

      {/* Results */}
      <main className="flex-1 max-w-[1200px] w-full mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10">
        {q.length < 2 && (
          <EmptyStart />
        )}

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-8" data-testid="sp-search-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Searching the network…
          </div>
        )}

        {error && !loading && (
          <div className="rounded-2xl bg-red-50 ring-1 ring-red-200 p-5 text-sm text-red-800" data-testid="sp-search-error">
            {error}
          </div>
        )}

        {!loading && !error && data && data.total === 0 && (
          <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-8 sm:p-10 text-center" data-testid="sp-search-empty">
            <Search className="w-8 h-8 text-slate-400 mx-auto" />
            <p className="mt-4 text-slate-700 text-base font-semibold">
              No matches for "<span className="text-slate-900">{q}</span>".
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Try broader words, a place name, or a topic like "housing" or "civic".
            </p>
          </div>
        )}

        {!loading && !error && data && data.total > 0 && (
          <ResultsBlock data={data} />
        )}
      </main>

      <SocialPulseFooter />
    </div>
  );
}

function ResultsBlock({ data }) {
  const summary = useMemo(() => {
    const n = data.total;
    const g = data.groups.length;
    return `${n} match${n === 1 ? "" : "es"} across ${g} section${g === 1 ? "" : "s"}`;
  }, [data]);

  return (
    <>
      <p className="mb-6 text-sm text-slate-500" data-testid="sp-search-summary">
        <span className="font-semibold text-slate-800">{summary}</span>
      </p>
      <div className="space-y-10">
        {data.groups.map((g) => <GroupSection key={g.type} group={g} />)}
      </div>
    </>
  );
}

function GroupSection({ group }) {
  const meta = TYPE_META[group.type] || {
    label: group.label, icon: Search, tint: "text-slate-700", bg: "bg-slate-50",
  };
  const Icon = meta.icon;
  const net = NETWORK_MAP[group.type];
  return (
    <section data-testid={`sp-search-group-${group.type}`}>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className={`w-9 h-9 grid place-items-center rounded-xl ${meta.bg} ${meta.tint}`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <h2 className="font-serif font-bold text-slate-900 text-lg sm:text-xl">
          {meta.label}
        </h2>
        {net && (
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
            {net.name} · {net.canonical}
          </span>
        )}
        <span className="ml-auto text-xs text-slate-400 font-medium">
          {group.count} result{group.count === 1 ? "" : "s"}
        </span>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {group.items.map((it) => <ResultCard key={`${it.type}-${it.id}`} item={it} meta={meta} />)}
      </ul>
    </section>
  );
}

function ResultCard({ item, meta }) {
  const Icon = meta.icon;
  const net = NETWORK_MAP[item.type];
  return (
    <li>
      <Link
        to={item.url || "#"}
        data-testid={`sp-search-result-${item.type}-${item.id}`}
        className="group flex items-start gap-3 sm:gap-4 rounded-2xl ring-1 ring-slate-200 bg-white p-4 hover:ring-blue-300 hover:shadow-md transition-all"
      >
        <div className={`w-14 h-14 rounded-xl overflow-hidden shrink-0 grid place-items-center ${meta.bg} ${meta.tint}`}>
          {item.image ? (
            <img
              src={item.image}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <Icon className="w-5 h-5" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="font-semibold text-slate-900 text-[15px] leading-snug line-clamp-2">
              {item.title || "Untitled"}
            </div>
            {net?.name && (
              <span
                data-testid={`sp-search-result-portal-${item.type}-${item.id}`}
                className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 ring-1 ring-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600"
                title={`Lives on ${net.name}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {net.name}
              </span>
            )}
          </div>
          {(item.subtitle || item.meta) && (
            <div className="mt-0.5 text-[11.5px] font-semibold uppercase tracking-wider text-slate-500">
              {[item.subtitle, item.meta].filter(Boolean).join(" · ")}
            </div>
          )}
          {item.snippet && (
            <p className="mt-1.5 text-[13px] text-slate-600 leading-snug line-clamp-2">
              {item.snippet}
            </p>
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-blue-600 shrink-0 mt-1 group-hover:translate-x-1 transition-transform" />
      </Link>
    </li>
  );
}

const SUGGESTIONS = [
  { label: "civic",      q: "civic" },
  { label: "kerala",     q: "kerala" },
  { label: "housing",    q: "housing" },
  { label: "community",  q: "community" },
  { label: "volunteer",  q: "volunteer" },
  { label: "memorial",   q: "memorial" },
];

function EmptyStart() {
  return (
    <div className="max-w-2xl">
      <p className="text-sm text-slate-500">
        Type at least two characters to search. Or try one of these:
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {SUGGESTIONS.map((s) => (
          <Link
            key={s.label}
            to={`/search?q=${encodeURIComponent(s.q)}`}
            data-testid={`sp-search-suggestion-${s.label}`}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-700 text-sm font-medium transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            {s.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
